import Component from '@glimmer/component';
import {
  PhCheckCircle,
  PhCircle,
  PhTrophy,
  PhStar,
  PhRocket,
  PhTarget,
  PhMedal,
  PhCrown,
} from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import style from 'ember-style-modifier/modifiers/style';
import { concat } from '@ember/helper';

/**
 * Milestone definition for debt journey
 */
export interface DebtMilestone {
  id: string;
  title: string;
  description: string;
  threshold: number; // percentage of debt paid
  achieved: boolean;
  achievedDate?: string;
  icon?: 'trophy' | 'star' | 'rocket' | 'target' | 'medal' | 'crown';
}

/**
 * MilestoneTracker - Achievement-style visualization of debt payoff milestones
 *
 * Shows a horizontal track of milestones with visual indicators for
 * achieved vs upcoming goals. Creates a gamified experience.
 *
 * @example
 * ```gts
 * <MilestoneTracker
 *   @milestones={{this.debtMilestones}}
 *   @currentProgress={{34}}
 * />
 * ```
 */
interface MilestoneTrackerSignature {
  Args: {
    /** Array of milestones to display */
    milestones: DebtMilestone[];
    /** Current progress percentage */
    currentProgress: number;
  };
  Element: HTMLDivElement;
}

const iconMap: Record<string, ComponentLike<{ Args: { size: number }; Element: SVGElement }>> = {
  trophy: PhTrophy,
  star: PhStar,
  rocket: PhRocket,
  target: PhTarget,
  medal: PhMedal,
  crown: PhCrown,
};

export default class MilestoneTracker extends Component<MilestoneTrackerSignature> {
  get nextMilestone(): DebtMilestone | undefined {
    return this.args.milestones.find((m) => !m.achieved);
  }

  get progressToNext(): number {
    if (!this.nextMilestone) return 0;
    return Math.max(0, this.nextMilestone.threshold - this.args.currentProgress);
  }

  getIcon = (iconName?: string) => iconMap[iconName || 'star'] || PhStar;

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div class="bg-white rounded-xl p-5 shadow-sm border border-gray-200" ...attributes>
      <div class="flex items-center gap-2 mb-4">
        <PhTrophy @size={{24}} class="text-amber-500" />
        <span class="font-semibold text-gray-900">Debt Freedom Milestones</span>
      </div>

      {{! Milestone Track }}
      <div class="relative">
        {{! Progress Line Background }}
        <div class="absolute top-6 left-0 right-0 h-1 bg-gray-200 rounded-full"></div>

        {{! Progress Line Fill }}
        <div
          class="absolute top-6 left-0 h-1 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all duration-500"
          {{style width=(concat @currentProgress "%")}}
        ></div>

        {{! Milestones }}
        <div class="relative flex justify-between">
          {{#each @milestones as |milestone|}}
            <div
              class="flex flex-col items-center"
              {{style width=(concat "calc(100% / " @milestones.length ")")}}
            >
              {{! Milestone Circle }}
              <div
                class="relative z-10 flex items-center justify-center w-12 h-12 rounded-full transition-all
                  {{if
                    milestone.achieved
                    'bg-gradient-to-br from-emerald-400 to-emerald-500 text-white shadow-lg shadow-emerald-200'
                    'bg-gray-100 text-gray-400 border-2 border-gray-200'
                  }}"
              >
                {{#if milestone.achieved}}
                  {{#let (this.getIcon milestone.icon) as |Icon|}}
                    <Icon @size={{22}} weight="fill" />
                  {{/let}}
                {{else}}
                  <PhCircle @size={{22}} />
                {{/if}}
              </div>

              {{! Milestone Label }}
              <div class="mt-3 text-center">
                <div
                  class="text-sm font-medium
                    {{if milestone.achieved 'text-gray-900' 'text-gray-500'}}"
                >
                  {{milestone.title}}
                </div>
                <div class="text-xs text-gray-400 mt-0.5">
                  {{milestone.description}}
                </div>
                {{#if milestone.achievedDate}}
                  <div class="flex items-center justify-center gap-1 mt-1 text-xs text-emerald-600">
                    <PhCheckCircle @size={{12}} weight="fill" />
                    <span>{{milestone.achievedDate}}</span>
                  </div>
                {{/if}}
              </div>
            </div>
          {{/each}}
        </div>
      </div>

      {{! Next Milestone Callout }}
      {{#if this.nextMilestone}}
        <div class="mt-6 pt-4 border-t border-gray-100">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <div class="p-1.5 bg-indigo-100 rounded-lg text-indigo-600">
                <PhTarget @size={{18}} />
              </div>
              <span class="text-sm text-gray-600">
                <span class="font-medium">Next milestone:</span>
                {{this.nextMilestone.title}}
              </span>
            </div>
            <span class="text-sm font-medium text-indigo-600">
              {{this.progressToNext}}% to go
            </span>
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
